import { Controller } from "@hotwired/stimulus";

interface GeoResult {
    display_name: string;
    address: string;
    number: string;
    city: string;
    state: string;
    district: string;
    postalCode: string;
    country: string;
    lat: string;
    lng: string;
}

export default class extends Controller {
    static values = {
        apiKey: String,
    };

    static targets = [
        "input",
        "address",
        "number",
        "country",
        "state",
        "city",
        "district",
        "postalCode",
        "lat",
        "lng",
    ];

    declare apiKeyValue: string;
    declare inputTarget: HTMLInputElement;
    declare addressTarget: HTMLInputElement;
    declare numberTarget: HTMLInputElement;
    declare countryTarget: HTMLInputElement;
    declare stateTarget: HTMLInputElement;
    declare cityTarget: HTMLInputElement;
    declare districtTarget: HTMLInputElement;
    declare postalCodeTarget: HTMLInputElement;
    declare latTarget: HTMLInputElement;
    declare lngTarget: HTMLInputElement;

    declare hasInputTarget: boolean;
    declare hasAddressTarget: boolean;
    declare hasNumberTarget: boolean;
    declare hasCountryTarget: boolean;
    declare hasStateTarget: boolean;
    declare hasCityTarget: boolean;
    declare hasDistrictTarget: boolean;
    declare hasPostalCodeTarget: boolean;
    declare hasLatTarget: boolean;
    declare hasLngTarget: boolean;

    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private dropdown: HTMLElement | null = null;
    private activeIndex = -1;
    private results: GeoResult[] = [];

    connect(): void {
        if (!this.hasInputTarget) return;
        this.inputTarget.addEventListener("input", this.onInput);
        this.inputTarget.addEventListener("keydown", this.onKeyDown);
        document.addEventListener("click", this.onDocumentClick);
    }

    disconnect(): void {
        this.inputTarget?.removeEventListener("input", this.onInput);
        this.inputTarget?.removeEventListener("keydown", this.onKeyDown);
        document.removeEventListener("click", this.onDocumentClick);
        this.removeDropdown();
    }

    fillFromSearch(): void {
        if (!this.hasInputTarget) return;
        const fallback = this.parseAddressLine(this.inputTarget.value);
        if (this.hasAddressTarget && fallback.route) {
            this.addressTarget.value = fallback.route;
        }
        if (this.hasNumberTarget && fallback.streetNumber) {
            this.numberTarget.value = fallback.streetNumber;
        }
    }

    private onInput = (): void => {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        const q = this.inputTarget.value.trim();
        if (q.length < 3) {
            this.removeDropdown();
            return;
        }
        this.debounceTimer = setTimeout(() => this.fetchSuggestions(q), 400);
    };

    private onKeyDown = (e: KeyboardEvent): void => {
        if (!this.dropdown) return;
        const items = this.dropdown.querySelectorAll<HTMLElement>(".geo-item");
        if (e.key === "ArrowDown") {
            e.preventDefault();
            this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
            this.updateActive(items);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            this.activeIndex = Math.max(this.activeIndex - 1, 0);
            this.updateActive(items);
        } else if (e.key === "Enter" && this.activeIndex >= 0) {
            e.preventDefault();
            this.selectResult(this.results[this.activeIndex]);
        } else if (e.key === "Escape") {
            this.removeDropdown();
        }
    };

    private onDocumentClick = (e: MouseEvent): void => {
        if (!this.dropdown) return;
        if (!this.element.contains(e.target as Node)) {
            this.removeDropdown();
        }
    };

    private async fetchSuggestions(q: string): Promise<void> {
        try {
            const resp = await fetch(`/api/geocode/search?q=${encodeURIComponent(q)}`);
            if (!resp.ok) return;
            this.results = await resp.json() as GeoResult[];
            this.showDropdown();
        } catch {
            // red de error — ignorar silenciosamente
        }
    }

    private showDropdown(): void {
        this.removeDropdown();
        if (!this.results.length) return;

        this.activeIndex = -1;
        this.dropdown = document.createElement("ul");
        this.dropdown.className = "list-group position-absolute w-100 shadow-sm geo-dropdown";
        this.dropdown.style.cssText = "z-index:1050;max-height:260px;overflow-y:auto;top:100%;left:0;";

        this.results.forEach((r, i) => {
            const li = document.createElement("li");
            li.className = "list-group-item list-group-item-action geo-item py-1 px-2";
            li.style.cursor = "pointer";
            li.style.fontSize = "0.85rem";
            li.textContent = r.display_name;
            li.addEventListener("mousedown", (e) => {
                e.preventDefault();
                this.selectResult(this.results[i]);
            });
            this.dropdown!.appendChild(li);
        });

        const wrapper = this.inputTarget.parentElement;
        if (wrapper) {
            wrapper.style.position = "relative";
            wrapper.appendChild(this.dropdown);
        }
    }

    private removeDropdown(): void {
        this.dropdown?.remove();
        this.dropdown = null;
        this.activeIndex = -1;
    }

    private updateActive(items: NodeListOf<HTMLElement>): void {
        items.forEach((el, i) => {
            el.classList.toggle("active", i === this.activeIndex);
        });
    }

    private selectResult(r: GeoResult): void {
        if (this.hasInputTarget)    this.inputTarget.value    = r.display_name;
        if (this.hasAddressTarget)  this.addressTarget.value  = r.address;
        if (this.hasNumberTarget)   this.numberTarget.value   = r.number;
        if (this.hasCountryTarget)  this.countryTarget.value  = r.country;
        if (this.hasStateTarget)    this.stateTarget.value    = r.state;
        if (this.hasCityTarget)     this.cityTarget.value     = r.city;
        if (this.hasDistrictTarget) this.districtTarget.value = r.district;
        if (this.hasPostalCodeTarget) this.postalCodeTarget.value = r.postalCode;
        if (this.hasLatTarget)      this.latTarget.value      = r.lat;
        if (this.hasLngTarget)      this.lngTarget.value      = r.lng;
        this.removeDropdown();
    }

    private parseAddressLine(address: string): Record<string, string> {
        const firstPart = address.split(",")[0]?.trim() || "";
        const match = firstPart.match(/^(.+?)\s+(\d+[a-zA-Z0-9\-\/]*)$/);
        if (!match) return { route: firstPart, streetNumber: "" };
        return { route: match[1].trim(), streetNumber: match[2].trim() };
    }
}
